import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AppLayout from "../components/AppLayout";
import CapsuleCard from "../components/CapsuleCard";
import ExploreSection from "../components/ExploreSection";
import { Rocket, Loader2, Package, Clock, Sparkles, Send, Inbox, Archive } from "lucide-react";
import { toast } from "sonner";

import { _t } from "@/lib/i18n";
export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState({ owned: [], received: [], counters: {} });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/capsules");
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const drafts = data.owned.filter((c) => c.state === "BOZZA");
  const reconsider = data.owned.filter((c) => c.state === "RIPENSAMENTO_24H");
  const inTransit = data.owned.filter((c) => c.state === "IN_VIAGGIO");
  const delivered = data.owned.filter((c) => c.state === "DISPONIBILE");
  const opened = data.owned.filter((c) => c.state === "APERTA");

  const receivedLocked = data.received.filter((c) => c.state === "IN_VIAGGIO");
  const receivedReady = data.received.filter((c) => c.state === "DISPONIBILE");
  const receivedOpened = data.received.filter((c) => c.state === "APERTA");

  const ownedTotal = data.owned.length;

  return (
    <AppLayout notificationCount={data.counters?.ready_to_open || 0}>
      {/* Hero */}
      <div className="grid md:grid-cols-3 gap-6 mb-12">
        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-3">{_t("La tua base di lancio")}</div>
          <h1 className="font-display text-5xl md:text-6xl text-white leading-tight mb-4">{_t("Ciao,")}{" "}<em className="text-[#FF5722] not-italic">{user?.name || user?.username}</em>
          </h1>
          <p className="text-white/60 max-w-xl">{_t("Il futuro è già qui. Prepara una capsula, sigillala, lasciala viaggiare nel tempo.")}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/wizard" data-testid="dashboard-create-btn" className="btn-launch">
              <Rocket className="w-4 h-4 -rotate-45" />{" "}{_t("Crea una capsula")}</Link>
          </div>
        </div>

        {receivedReady.length > 0 && (
          <div className="tc-card p-6 relative overflow-hidden" data-testid="ready-panel">
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#FF5722]/15 blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-2 text-[#FF5722] mb-2">
                <Sparkles className="w-4 h-4" />
                <span className="text-[10px] uppercase tracking-[0.3em]">{_t("Pronte da aprire")}</span>
              </div>
              <div className="font-display text-5xl text-white mb-2">{receivedReady.length}</div>
              <p className="text-sm text-white/60 mb-4">{_t("Alcune capsule hanno raggiunto la loro data.")}</p>
              <Link to={`/capsule/${receivedReady[0].id}`} className="btn-ghost text-sm" data-testid="ready-first-btn">{_t("Vai alla prima ↗")}</Link>
            </div>
          </div>
        )}
      </div>

      {/* Stat boxes */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Stat label={_t("Bozze")} value={drafts.length} icon={<Package className="w-4 h-4" />} testid="stat-drafts" />
        <Stat label={_t("Ripensamento")} value={reconsider.length} icon={<Clock className="w-4 h-4" />} testid="stat-reconsider" />
        <Stat label={_t("In viaggio")} value={inTransit.length + delivered.length} icon={<Rocket className="w-4 h-4" />} testid="stat-in-transit" />
        <Stat label={_t("Ricevute")} value={data.received.length} icon={<Inbox className="w-4 h-4" />} testid="stat-received" />
      </div>

      {/* Esplora — anteprime delle sezioni pubbliche */}
      <ExploreSection />

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-8 border-b border-white/10 overflow-x-auto">
        <TabBtn active={tab === "all"} onClick={() => setTab("all")} testid="tab-all">{_t("Tutte")}</TabBtn>
        <TabBtn active={tab === "sent"} onClick={() => setTab("sent")} testid="tab-sent" icon={<Send className="w-4 h-4" />}>{_t("Inviate ({0})", ownedTotal)}</TabBtn>
        <TabBtn active={tab === "received"} onClick={() => setTab("received")} testid="tab-received" icon={<Inbox className="w-4 h-4" />}>{_t("Ricevute ({0})", data.received.length)}</TabBtn>
        <TabBtn active={tab === "drafts"} onClick={() => setTab("drafts")} testid="tab-drafts">{_t("Bozze ({0})", drafts.length)}</TabBtn>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#FF5722]" /></div>
      ) : (
        <>
          {tab === "all" && (
            <>
              {receivedReady.length > 0 && <Section title={_t("Pronte da aprire")} caps={receivedReady} />}
              {reconsider.length > 0 && <Section title={_t("Periodo di ripensamento (24h)")} caps={reconsider} />}
              {inTransit.length > 0 && <Section title={_t("In viaggio")} caps={inTransit} />}
              {receivedLocked.length > 0 && <Section title={_t("In arrivo per te")} caps={receivedLocked} />}
              {drafts.length > 0 && <Section title={_t("Bozze")} caps={drafts} />}
              {opened.length > 0 && <Section title={_t("Consegnate e aperte")} caps={opened} />}
              {receivedOpened.length > 0 && <Section title={_t("Ricevute e aperte")} caps={receivedOpened} />}
              {data.owned.length === 0 && data.received.length === 0 && <EmptyState />}
            </>
          )}
          {tab === "sent" && (data.owned.length > 0 ? <Grid caps={data.owned} /> : <EmptyState tab="sent" />)}
          {tab === "received" && (data.received.length > 0 ? <Grid caps={data.received} /> : <EmptyState tab="received" />)}
          {tab === "drafts" && (drafts.length > 0 ? <Grid caps={drafts} /> : <EmptyState tab="drafts" />)}
        </>
      )}
    </AppLayout>
  );
}

function Stat({ label, value, icon, testid }) {
  return (
    <div className="tc-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-white/50 text-[10px] uppercase tracking-[0.3em] mb-2">
        {icon} {label}
      </div>
      <div className="font-display text-4xl text-white">{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid, icon }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`px-4 py-3 -mb-px text-sm font-medium inline-flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${
        active ? "border-[#FF5722] text-[#FF5722]" : "border-transparent text-white/60 hover:text-white"
      }`}>
      {icon}{children}
    </button>
  );
}

function Section({ title, caps }) {
  return (
    <div className="mb-10">
      <div className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-4">{title}</div>
      <Grid caps={caps} />
    </div>
  );
}

function Grid({ caps }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="capsules-grid">
      {caps.map((c) => <CapsuleCard key={c.id} capsule={c} />)}
    </div>
  );
}

function EmptyState({ tab }) {
  const copy = tab === "sent"
    ? { title: _t("Nessuna capsula inviata"), sub: _t("Crea la tua prima capsula per iniziare a inviare pensieri nel futuro.") }
    : tab === "received"
    ? { title: _t("Nessuna capsula ricevuta"), sub: _t("Quando qualcuno ti invierà una capsula, la troverai qui.") }
    : tab === "drafts"
    ? { title: _t("Nessuna bozza"), sub: _t("Le capsule che stai preparando appariranno qui.") }
    : { title: _t("La tua rampa di lancio è vuota"), sub: _t("Crea o ricevi una capsula per iniziare.") };
  return (
    <div className="tc-card p-16 text-center" data-testid="empty-state">
      <Archive className="w-8 h-8 text-white/30 mx-auto mb-4" />
      <div className="font-display text-3xl text-white mb-3">{copy.title}</div>
      <p className="text-white/60 mb-8 max-w-md mx-auto">{copy.sub}</p>
      <Link to="/wizard" className="btn-launch inline-flex"><Rocket className="w-4 h-4 -rotate-45" />{" "}{_t("Crea una capsula")}</Link>
    </div>
  );
}
